import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { damagesAPI, unitsAPI, propertiesAPI, tenantsAPI } from '../lib/api';
import { formatDate, formatCurrency } from '../lib/utils';
import { toast } from 'sonner';
import {
  Plus,
  AlertTriangle,
  Edit,
  Trash2,
  Loader2,
  Search,
  CheckCircle,
  Clock,
  AlertCircle,
  Wrench
} from 'lucide-react';

const CATEGORY_LABELS = {
  sanitaer: 'Sanitär',
  elektrik: 'Elektrik',
  heizung: 'Heizung',
  fenster_tueren: 'Fenster & Türen',
  dach: 'Dach',
  fassade: 'Fassade',
  wasserschaden: 'Wasserschaden',
  schimmel: 'Schimmel',
  aufzug: 'Aufzug',
  sonstiges: 'Sonstiges'
};

const PRIORITY_LABELS = {
  niedrig: 'Niedrig',
  mittel: 'Mittel',
  hoch: 'Hoch',
  dringend: 'Dringend'
};

const STATUS_LABELS = {
  offen: 'Offen',
  in_bearbeitung: 'In Bearbeitung',
  behoben: 'Behoben'
};

const PRIORITY_COLORS = {
  niedrig: 'bg-slate-100 text-slate-700',
  mittel: 'bg-blue-100 text-blue-700',
  hoch: 'bg-amber-100 text-amber-700',
  dringend: 'bg-red-100 text-red-700'
};

const STATUS_COLORS = {
  offen: 'bg-red-100 text-red-700',
  in_bearbeitung: 'bg-amber-100 text-amber-700',
  behoben: 'bg-emerald-100 text-emerald-700'
};

export default function Damages() {
  const [damages, setDamages] = useState([]);
  const [units, setUnits] = useState([]);
  const [properties, setProperties] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterProperty, setFilterProperty] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingDamage, setEditingDamage] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [formData, setFormData] = useState({
    unit_id: '',
    tenant_id: '',
    title: '',
    description: '',
    category: 'sonstiges',
    priority: 'mittel',
    status: 'offen',
    resolution_notes: '',
    repair_cost: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [damagesRes, unitsRes, propsRes, tenantsRes, statsRes] = await Promise.all([
        damagesAPI.getAll(),
        unitsAPI.getAll(),
        propertiesAPI.getAll(),
        tenantsAPI.getAll(),
        damagesAPI.getStats()
      ]);
      setDamages(damagesRes.data);
      setUnits(unitsRes.data);
      setProperties(propsRes.data);
      setTenants(tenantsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      unit_id: units.length > 0 ? units[0].id : '',
      tenant_id: '',
      title: '',
      description: '',
      category: 'sonstiges',
      priority: 'mittel',
      status: 'offen',
      resolution_notes: '',
      repair_cost: ''
    });
    setEditingDamage(null);
  };

  const openEditDialog = (damage) => {
    setEditingDamage(damage);
    setFormData({
      unit_id: damage.unit_id,
      tenant_id: damage.tenant_id || '',
      title: damage.title,
      description: damage.description,
      category: damage.category,
      priority: damage.priority,
      status: damage.status,
      resolution_notes: damage.resolution_notes || '',
      repair_cost: damage.repair_cost || ''
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const data = {
        ...formData,
        tenant_id: formData.tenant_id || null,
        repair_cost: formData.repair_cost ? parseFloat(formData.repair_cost) : null,
        resolution_notes: formData.resolution_notes || null
      };

      if (editingDamage) {
        await damagesAPI.update(editingDamage.id, data);
        toast.success('Schaden aktualisiert');
      } else {
        await damagesAPI.create(data);
        toast.success('Schaden erfasst');
      }

      setIsDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      await damagesAPI.delete(deleteId);
      toast.success('Schaden gelöscht');
      loadData();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    } finally {
      setDeleteId(null);
    }
  };

  const getUnitName = (unitId) => {
    const unit = units.find(u => u.id === unitId);
    return unit ? unit.name : 'Unbekannt';
  };

  const getPropertyName = (propertyId) => {
    const prop = properties.find(p => p.id === propertyId);
    return prop ? prop.name : 'Unbekannt';
  };

  const getTenantsForUnit = (unitId) => {
    return tenants.filter(t => t.unit_id === unitId && t.is_active);
  };

  const filteredDamages = damages.filter(d => {
    const matchesSearch = d.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || d.status === filterStatus;
    const matchesProperty = filterProperty === 'all' || d.property_id === filterProperty;
    return matchesSearch && matchesStatus && matchesProperty;
  });

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="damages-page" className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Schadensmanagement
            </h1>
            <p className="text-slate-500 mt-1">Verwalten Sie Schäden und Reparaturen</p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={(open) => {
            setIsDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button 
                data-testid="add-damage-button" 
                className="bg-slate-900 hover:bg-slate-800"
                disabled={units.length === 0}
              >
                <Plus className="mr-2 h-4 w-4" />
                Schaden melden
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[550px] max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingDamage ? 'Schaden bearbeiten' : 'Neuer Schaden'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2 space-y-2">
                    <Label htmlFor="unit_id">
                      Einheit <span className="text-red-500">*</span>
                    </Label>
                    <Select
                      value={formData.unit_id}
                      onValueChange={(value) => setFormData({ ...formData, unit_id: value, tenant_id: '' })}
                      required
                    >
                      <SelectTrigger data-testid="damage-unit-select">
                        <SelectValue placeholder="Einheit auswählen" />
                      </SelectTrigger>
                      <SelectContent>
                        {units.map((unit) => {
                          const prop = properties.find(p => p.id === unit.property_id);
                          return (
                            <SelectItem key={unit.id} value={unit.id}>
                              {unit.name} ({prop?.name})
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>

                  {formData.unit_id && getTenantsForUnit(formData.unit_id).length > 0 && (
                    <div className="col-span-2 space-y-2">
                      <Label htmlFor="tenant_id">Mieter (optional)</Label>
                      <Select
                        value={formData.tenant_id}
                        onValueChange={(value) => setFormData({ ...formData, tenant_id: value === 'none' ? '' : value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Mieter auswählen" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Kein Mieter zugeordnet</SelectItem>
                          {getTenantsForUnit(formData.unit_id).map((tenant) => (
                            <SelectItem key={tenant.id} value={tenant.id}>
                              {tenant.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div className="col-span-2 space-y-2">
                    <Label htmlFor="title">
                      Titel <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="title"
                      data-testid="damage-title-input"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      placeholder="z.B. Wasserhahn tropft"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="category">Kategorie</Label>
                    <Select
                      value={formData.category}
                      onValueChange={(value) => setFormData({ ...formData, category: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
                          <SelectItem key={value} value={value}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="priority">Priorität</Label>
                    <Select
                      value={formData.priority}
                      onValueChange={(value) => setFormData({ ...formData, priority: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(PRIORITY_LABELS).map(([value, label]) => (
                          <SelectItem key={value} value={value}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="col-span-2 space-y-2">
                    <Label htmlFor="description">
                      Beschreibung <span className="text-red-500">*</span>
                    </Label>
                    <Textarea
                      id="description"
                      data-testid="damage-description-input"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Detaillierte Beschreibung des Schadens..."
                      rows={3}
                      required
                    />
                  </div>

                  {editingDamage && (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="status">Status</Label>
                        <Select
                          value={formData.status}
                          onValueChange={(value) => setFormData({ ...formData, status: value })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(STATUS_LABELS).map(([value, label]) => (
                              <SelectItem key={value} value={value}>
                                {label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="repair_cost">Reparaturkosten (€)</Label>
                        <Input
                          id="repair_cost"
                          type="number"
                          step="0.01"
                          value={formData.repair_cost}
                          onChange={(e) => setFormData({ ...formData, repair_cost: e.target.value })}
                          placeholder="0.00"
                        />
                      </div>

                      <div className="col-span-2 space-y-2">
                        <Label htmlFor="resolution_notes">Lösungsnotizen</Label>
                        <Textarea
                          id="resolution_notes"
                          value={formData.resolution_notes}
                          onChange={(e) => setFormData({ ...formData, resolution_notes: e.target.value })}
                          placeholder="Notizen zur Behebung des Schadens..."
                          rows={2}
                        />
                      </div>
                    </>
                  )}
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsDialogOpen(false)}
                  >
                    Abbrechen
                  </Button>
                  <Button type="submit" disabled={saving} data-testid="damage-save-button">
                    {saving ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Speichern...
                      </>
                    ) : (
                      'Speichern'
                    )}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                    <AlertCircle className="h-5 w-5 text-red-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{stats.open_damages}</p>
                    <p className="text-sm text-slate-500">Offen</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                    <Wrench className="h-5 w-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{stats.in_progress}</p>
                    <p className="text-sm text-slate-500">In Bearbeitung</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <CheckCircle className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{stats.resolved}</p>
                    <p className="text-sm text-slate-500">Behoben</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                    <AlertTriangle className="h-5 w-5 text-slate-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
                    <p className="text-sm text-slate-500">Gesamt</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              data-testid="damage-search-input"
              placeholder="Suchen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={filterProperty} onValueChange={setFilterProperty}>
            <SelectTrigger className="w-full sm:w-[200px]">
              <SelectValue placeholder="Alle Immobilien" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Immobilien</SelectItem>
              {properties.map((prop) => (
                <SelectItem key={prop.id} value={prop.id}>
                  {prop.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-full sm:w-[180px]">
              <SelectValue placeholder="Alle Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Status</SelectItem>
              {Object.entries(STATUS_LABELS).map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Damages List */}
        {filteredDamages.length > 0 ? (
          <Card className="border-slate-200">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/50">
                  <TableHead className="font-semibold">Schaden</TableHead>
                  <TableHead className="font-semibold">Einheit</TableHead>
                  <TableHead className="font-semibold">Kategorie</TableHead>
                  <TableHead className="font-semibold">Priorität</TableHead>
                  <TableHead className="font-semibold">Status</TableHead>
                  <TableHead className="font-semibold">Gemeldet</TableHead>
                  <TableHead className="w-[100px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredDamages.map((damage) => (
                  <TableRow key={damage.id} data-testid={`damage-row-${damage.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center">
                          <AlertTriangle className="h-5 w-5 text-red-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{damage.title}</p>
                          <p className="text-sm text-slate-500 truncate max-w-[200px]">
                            {damage.description}
                          </p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="text-slate-700">{getUnitName(damage.unit_id)}</p>
                        <p className="text-sm text-slate-500">{getPropertyName(damage.property_id)}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="bg-slate-100 text-slate-700">
                        {CATEGORY_LABELS[damage.category] || damage.category}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={PRIORITY_COLORS[damage.priority]}>
                        {PRIORITY_LABELS[damage.priority] || damage.priority}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={STATUS_COLORS[damage.status]}>
                        {STATUS_LABELS[damage.status] || damage.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-slate-600 text-sm">
                      {formatDate(damage.reported_date)}
                      {damage.reported_by && (
                        <p className="text-xs text-slate-400">von {damage.reported_by}</p>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditDialog(damage)}
                        >
                          <Edit className="h-4 w-4 text-slate-500" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setDeleteId(damage.id)}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        ) : (
          <Card className="border-slate-200">
            <CardContent className="py-12 text-center">
              <AlertTriangle className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">
                {searchTerm || filterStatus !== 'all' || filterProperty !== 'all'
                  ? 'Keine Schäden gefunden'
                  : 'Noch keine Schäden'}
              </h3>
              <p className="text-slate-500 mb-4">
                {searchTerm || filterStatus !== 'all' || filterProperty !== 'all'
                  ? 'Versuchen Sie andere Filtereinstellungen'
                  : 'Erfassen Sie den ersten Schaden'}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Delete Confirmation */}
        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Schaden löschen?</AlertDialogTitle>
              <AlertDialogDescription>
                Diese Aktion kann nicht rückgängig gemacht werden.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Abbrechen</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700"
              >
                Löschen
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
